import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { formatApiMessage } from '@/api/client';
import { envelopesApi, type EnvelopeView, type EnvelopeWorkflow } from '@/api/envelopes.api';
import {
  type Recipient,
  type RecipientAuthMethod,
  type RecipientRole,
  recipientsApi,
} from '@/api/recipients.api';
import { type GroupMember, recipientGroupsApi } from '@/api/recipient-groups.api';
import { usersApi } from '@/api/users.api';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth.store';

const ROLE_VALUES: RecipientRole[] = ['signer', 'approver', 'for_info', 'cc', 'witness'];
// SMS OTP intentionally omitted — not offered to users (no SMS provider in use).
const AUTH_VALUES: RecipientAuthMethod[] = ['email_otp', 'access_code', 'none'];

const formSchema = z.object({
  email: z.string().email('Invalid email'),
  name: z.string().min(1, 'Required').max(200),
  role: z.enum(['signer', 'approver', 'cc', 'witness', 'for_info']),
  authMethod: z.enum(['email_otp', 'sms_otp', 'access_code', 'none']),
  phone: z.string().max(30).optional().or(z.literal('')),
  accessCode: z
    .string()
    .min(4)
    .max(20)
    .regex(/^[A-Za-z0-9]+$/, 'Alphanumeric only')
    .optional()
    .or(z.literal('')),
  customMessage: z.string().max(2000).optional().or(z.literal('')),
});
type FormValues = z.infer<typeof formSchema>;

type Props = {
  envelope: EnvelopeView;
};

export function RecipientsStep({ envelope }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const myRole = me?.role;
  const myEmail = me?.email.toLowerCase();
  const canReorder = myRole === 'owner' || myRole === 'admin';
  // Approval-sequence templates ("PR təsdiqi şablonu") are defined by admins
  // only (matches the backend `@Roles('admin')` guard on POST
  // /recipient-groups). Everyone can still load/apply an existing template.
  const isAdmin = myRole === 'owner' || myRole === 'admin';

  const { data: recipients = [] } = useQuery({
    queryKey: ['recipients', envelope.id],
    queryFn: () => recipientsApi.list(envelope.id),
  });

  const workflowMutation = useMutation({
    mutationFn: (workflow: EnvelopeWorkflow) =>
      envelopesApi.update(envelope.id, { workflowType: workflow }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['envelopes', 'detail', envelope.id] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });


  // Per-envelope reminder timing (enable + interval days + hour of day).
  const reminderMutation = useMutation({
    mutationFn: (patch: {
      reminderEnabled?: boolean;
      reminderDays?: number;
      reminderHour?: number;
    }) => envelopesApi.update(envelope.id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['envelopes', 'detail', envelope.id] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const moveMutation = useMutation({
    mutationFn: (items: Array<{ id: string; signingOrder: number }>) =>
      recipientsApi.reorder(envelope.id, items),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recipients', envelope.id] }),
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const removeMutation = useMutation({
    mutationFn: recipientsApi.remove,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recipients', envelope.id] });
      toast.success(t('recipients_step.recipient_removed'));
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['recipient-groups'],
    queryFn: () => recipientGroupsApi.list(),
  });

  // Apply group: insert every member of the chosen template, preserving
  // signingOrder relative to whatever's already there. We do it serially
  // so the backend's order-collision logic stays sane.
  const applyGroupMutation = useMutation({
    mutationFn: async (members: GroupMember[]) => {
      const base = recipients.length;
      for (let i = 0; i < members.length; i++) {
        const m = members[i];
        await recipientsApi.create(envelope.id, {
          email: m.email,
          name: m.name,
          role: m.role as RecipientRole,
          authMethod: m.authMethod as RecipientAuthMethod,
          signingOrder: base + m.signingOrder,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recipients', envelope.id] });
      toast.success(t('recipient_groups.applied_toast'));
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const saveGroupMutation = useMutation({
    mutationFn: (input: { name: string; description?: string }) =>
      recipientGroupsApi.create({
        name: input.name,
        description: input.description,
        members: recipients
          .slice()
          .sort((a, b) => a.signingOrder - b.signingOrder)
          .map((r, i) => ({
            email: r.email,
            name: r.name,
            role: r.role,
            authMethod: r.authMethod,
            signingOrder: i + 1,
          })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recipient-groups'] });
      toast.success(t('recipient_groups.saved_toast'));
      setShowSaveGroup(false);
      setNewGroupName('');
      setNewGroupDesc('');
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const [pickedGroup, setPickedGroup] = useState('');
  const [showSaveGroup, setShowSaveGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');

  function move(uuid: string, direction: 'up' | 'down') {
    const ordered = [...recipients].sort((a, b) => a.signingOrder - b.signingOrder);
    const idx = ordered.findIndex((r) => r.id === uuid);
    if (idx < 0) return;
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= ordered.length) return;
    [ordered[idx], ordered[target]] = [ordered[target], ordered[idx]];
    moveMutation.mutate(
      ordered.map((r, i) => ({ id: r.id, signingOrder: i + 1 })),
    );
  }

  return (
    <div>
      <h3 className="text-base font-semibold text-gray-900 mb-1">{t('recipients_step.title')}</h3>
      <p className="text-[13px] text-muted mb-4">{t('recipients_step.subtitle')}</p>

      {/* Workflow toggle */}
      <div className="card !p-3 mb-5">
        <div className="text-[12px] font-semibold text-gray-900 mb-2">{t('recipients_step.workflow_title')}</div>
        <div className="flex gap-2">
          {(['sequential', 'parallel'] as const).map((wf) => (
            <button
              key={wf}
              onClick={() => workflowMutation.mutate(wf)}
              className={cn(
                'flex-1 px-3 py-2 rounded-md border text-[13px] text-left',
                envelope.workflowType === wf
                  ? 'border-primary bg-primary-50 text-primary'
                  : 'border-border bg-white text-gray-700 hover:border-border-strong',
              )}
            >
              <div className="font-semibold">
                {wf === 'sequential' ? t('recipients_step.workflow_sequential') : t('recipients_step.workflow_parallel')}
              </div>
              <div className="text-xs text-muted mt-0.5">
                {wf === 'sequential'
                  ? t('recipients_step.workflow_sequential_desc')
                  : t('recipients_step.workflow_parallel_desc')}
              </div>
            </button>
          ))}
        </div>

        {/* Per-envelope reminder timing — when unsigned recipients get nudged. */}
        <div className="mt-3 pt-3 border-t border-border">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={envelope.reminderEnabled}
              disabled={reminderMutation.isPending}
              onChange={(e) =>
                reminderMutation.mutate({
                  reminderEnabled: e.target.checked,
                  ...(e.target.checked && envelope.reminderDays == null
                    ? { reminderDays: 3, reminderHour: 9 }
                    : {}),
                })
              }
              className="mt-0.5 accent-primary"
            />
            <span className="text-[13px]">
              <span className="font-medium text-gray-900">{t('recipients_step.reminder_label')}</span>
              <span className="block text-xs text-muted mt-0.5">{t('recipients_step.reminder_hint')}</span>
            </span>
          </label>
          {envelope.reminderEnabled && (
            <div className="flex items-center gap-2 mt-2 ml-6 flex-wrap text-[13px] text-muted">
              <span>{t('recipients_step.reminder_every')}</span>
              <input
                type="number"
                min={1}
                max={90}
                defaultValue={envelope.reminderDays ?? 3}
                onBlur={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (v >= 1 && v <= 90) reminderMutation.mutate({ reminderDays: v });
                }}
                className="input !py-1 !px-2 w-16 text-center text-gray-900"
              />
              <span>{t('recipients_step.reminder_days_at')}</span>
              <input
                type="number"
                min={0}
                max={23}
                defaultValue={envelope.reminderHour ?? 9}
                onBlur={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (v >= 0 && v <= 23) reminderMutation.mutate({ reminderHour: v });
                }}
                className="input !py-1 !px-2 w-16 text-center text-gray-900"
              />
              <span>{t('recipients_step.reminder_oclock')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Saved recipient groups — quick-load chain templates */}
      <div className="card !p-3 mb-5">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-[12px] font-semibold text-gray-900">
            💾 {t('recipient_groups.section_title')}
          </div>
          {isAdmin && recipients.length > 0 && (
            <button
              type="button"
              onClick={() => setShowSaveGroup(true)}
              className="text-[11px] text-primary hover:underline"
              title={t('recipient_groups.save_as_hint') as string}
            >
              + {t('recipient_groups.save_as')}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={pickedGroup}
            onChange={(e) => setPickedGroup(e.target.value)}
            className="input text-[12px] flex-1 min-w-[200px]"
          >
            <option value="">{t('recipient_groups.pick_placeholder')}</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name} ({g.members.length} {t('recipient_groups.members_short')})
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!pickedGroup || applyGroupMutation.isPending}
            onClick={() => {
              const g = groups.find((x) => x.id === pickedGroup);
              if (g) applyGroupMutation.mutate(g.members);
            }}
            className="btn btn-secondary btn-sm"
          >
            {applyGroupMutation.isPending
              ? t('recipient_groups.applying')
              : `+ ${t('recipient_groups.apply')}`}
          </button>
        </div>
        {groups.length === 0 && (
          <p className="text-[11px] text-muted mt-2">
            {t('recipient_groups.empty_hint')}
          </p>
        )}
      </div>

      {/* Save current recipients as group — modal (admin only) */}
      {isAdmin && showSaveGroup && (
        <div
          className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowSaveGroup(false)}
        >
          <div
            className="bg-white rounded-lg border border-border w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border">
              <h3 className="text-base font-semibold text-gray-900">
                💾 {t('recipient_groups.save_modal_title')}
              </h3>
              <p className="text-[12px] text-muted mt-1">
                {t('recipient_groups.save_modal_subtitle', { count: recipients.length })}
              </p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="label">{t('recipient_groups.name_label')}</label>
                <input
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder={t('recipient_groups.name_placeholder') as string}
                  maxLength={150}
                  className="input"
                  autoFocus
                />
              </div>
              <div>
                <label className="label">{t('recipient_groups.description_label')}</label>
                <textarea
                  value={newGroupDesc}
                  onChange={(e) => setNewGroupDesc(e.target.value)}
                  placeholder={t('recipient_groups.description_placeholder') as string}
                  maxLength={500}
                  rows={3}
                  className="textarea"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
              <button
                onClick={() => setShowSaveGroup(false)}
                className="btn btn-secondary"
                disabled={saveGroupMutation.isPending}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() =>
                  saveGroupMutation.mutate({
                    name: newGroupName,
                    description: newGroupDesc || undefined,
                  })
                }
                disabled={!newGroupName.trim() || saveGroupMutation.isPending}
                className="btn btn-primary"
              >
                {saveGroupMutation.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recipient list */}
      <div className="card !p-0 mb-5">
        {recipients.length === 0 ? (
          <div className="text-center py-12 px-4">
            <div className="text-3xl mb-2">👥</div>
            <div className="font-medium text-gray-900">{t('recipients_step.no_recipients_yet')}</div>
            <div className="text-[13px] text-muted mt-1">{t('recipients_step.add_first_signer')}</div>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {recipients
              .slice()
              .sort((a, b) => a.signingOrder - b.signingOrder)
              .map((r, i) => (
                <RecipientRow
                  key={r.id}
                  recipient={r}
                  index={i}
                  total={recipients.length}
                  isSequential={envelope.workflowType === 'sequential'}
                  canReorder={canReorder}
                  isMe={!!myEmail && r.email.toLowerCase() === myEmail}
                  onMove={(dir) => move(r.id, dir)}
                  onRemove={() => {
                    if (confirm(t('recipients_step.remove_confirm', { email: r.email }))) {
                      removeMutation.mutate(r.id);
                    }
                  }}
                />
              ))}
          </ul>
        )}
      </div>

      {/* Add form */}
      <AddRecipientForm envelopeId={envelope.id} />
    </div>
  );
}

function RecipientRow({
  recipient,
  index,
  total,
  isSequential,
  canReorder,
  isMe,
  onMove,
  onRemove,
}: {
  recipient: Recipient;
  index: number;
  total: number;
  isSequential: boolean;
  canReorder: boolean;
  isMe: boolean;
  onMove: (dir: 'up' | 'down') => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  // Color-cycle the avatar so multiple recipients are visually distinct —
  // matches the field-marker colours used on the Fields step.
  const avatarColors = [
    '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#0ea5e9',
  ];
  const avatarBg = avatarColors[index % avatarColors.length];
  const initials = recipient.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <li className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/50 transition-colors">
      {isSequential && (
        <div className="text-[12px] font-semibold text-muted w-6 text-center shrink-0">
          #{recipient.signingOrder}
        </div>
      )}
      <div
        className="w-9 h-9 rounded-full text-white flex items-center justify-center text-[12px] font-semibold shrink-0 border-2 border-white shadow-sm"
        style={{ background: avatarBg }}
      >
        {initials || (recipient.name[0] ?? '?').toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900 truncate">{recipient.name}</span>
          {isMe && (
            <span className="badge badge-info text-[10px]" title={t('recipients_step.you_hint')}>
              {t('recipients_step.you_badge')}
            </span>
          )}
          <span className="badge badge-muted text-[10px]">
            {t(`recipients_step.role_${recipient.role}`)}
          </span>
        </div>
        <div className="text-[12px] text-muted truncate">
          {recipient.email}
          {recipient.authMethod !== 'email_otp' && ` · ${t(`recipients_step.auth_${recipient.authMethod}`)}`}
        </div>
      </div>
      {isSequential && canReorder && (
        <div className="flex flex-col gap-0.5">
          <button
            onClick={() => onMove('up')}
            disabled={index === 0}
            className="px-1.5 text-muted hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
            title={t('recipients_step.move_up')}
          >
            ▲
          </button>
          <button
            onClick={() => onMove('down')}
            disabled={index === total - 1}
            className="px-1.5 text-muted hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
            title={t('recipients_step.move_down')}
          >
            ▼
          </button>
        </div>
      )}
      {isSequential && !canReorder && (
        <span className="text-[10px] text-muted px-1.5" title={t('recipients_step.reorder_admin_only')}>🔒</span>
      )}
      <button
        onClick={onRemove}
        className="btn btn-ghost btn-sm text-danger"
        title={isMe ? t('recipients_step.remove_self_hint') : undefined}
      >
        {t('recipients_step.remove')}
      </button>
    </li>
  );
}

function AddRecipientForm({ envelopeId }: { envelopeId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showTeamPicker, setShowTeamPicker] = useState(false);
  const [allowExternal, setAllowExternal] = useState(false);

  const { data: teamMembers = [] } = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => usersApi.list(),
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { role: 'signer', authMethod: 'email_otp' },
  });

  const authMethod = watch('authMethod');
  const emailValue = watch('email') ?? '';
  const isTeamMember = teamMembers.some(
    (u) => u.email.toLowerCase() === emailValue.toLowerCase().trim(),
  );
  const showExternalWarning =
    !!emailValue && !isTeamMember && !allowExternal && /^\S+@\S+\.\S+$/.test(emailValue);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const matches = teamMembers.some(
        (u) => u.email.toLowerCase() === values.email.toLowerCase().trim(),
      );
      if (!matches && !allowExternal) {
        return Promise.reject(new Error(t('recipients_step.not_in_team_error')));
      }
      return recipientsApi.create(envelopeId, {
        email: values.email,
        name: values.name,
        role: values.role,
        authMethod: values.authMethod,
        phone: values.phone || undefined,
        accessCode: values.accessCode || undefined,
        customMessage: values.customMessage || undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recipients', envelopeId] });
      qc.invalidateQueries({ queryKey: ['envelopes', 'detail', envelopeId] });
      reset({ role: 'signer', authMethod: 'email_otp' });
      setAllowExternal(false);
      toast.success(t('recipients_step.recipient_added'));
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <form
      onSubmit={handleSubmit((v) => mutation.mutate(v))}
      className="card"
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900">{t('recipients_step.add_recipient')}</h4>
        <button
          type="button"
          onClick={() => setShowTeamPicker(true)}
          className="text-[12px] text-primary hover:underline"
        >
          {t('recipients_step.pick_from_team')}
        </button>
      </div>
      {showTeamPicker && (
        <TeamPicker
          onClose={() => setShowTeamPicker(false)}
          onPick={(u) => {
            setValue('name', `${u.firstName} ${u.lastName}`);
            setValue('email', u.email);
            setShowTeamPicker(false);
          }}
        />
      )}

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="label">{t('recipients_step.name_label')}</label>
          <input className="input" {...register('name')} placeholder={t('recipients_step.name_placeholder')} />
          {errors.name && <p className="text-xs text-danger mt-1">{errors.name.message}</p>}
        </div>
        <div>
          <label className="label">{t('recipients_step.email_label')}</label>
          <input className="input" type="email" {...register('email')} placeholder={t('recipients_step.email_placeholder')} />
          {errors.email && <p className="text-xs text-danger mt-1">{errors.email.message}</p>}
          {emailValue && isTeamMember && (
            <p className="text-xs text-success mt-1">{t('recipients_step.is_team_member')}</p>
          )}
          {showExternalWarning && (
            <p className="text-xs text-warning mt-1">{t('recipients_step.not_team_warning')}</p>
          )}
        </div>
      </div>

      <div className="mb-3">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={allowExternal}
            onChange={(e) => setAllowExternal(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-[12px] text-gray-700">
            <span className="font-medium">{t('recipients_step.external_signer')}</span> — {t('recipients_step.external_signer_hint')}
            <span className="block text-muted text-[11px] mt-0.5">
              {t('recipients_step.external_signer_desc')}
            </span>
          </span>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="label">{t('recipients_step.role_label')}</label>
          <select className="select" {...register('role')}>
            {ROLE_VALUES.map((value) => (
              <option key={value} value={value}>
                {t(`recipients_step.role_${value}`)} — {t(`recipients_step.role_${value}_hint`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">{t('recipients_step.auth_label')}</label>
          <select className="select" {...register('authMethod')}>
            {AUTH_VALUES.map((value) => (
              <option key={value} value={value}>{t(`recipients_step.auth_${value}`)}</option>
            ))}
          </select>
        </div>
      </div>

      {authMethod === 'sms_otp' && (
        <div className="mb-3">
          <label className="label">{t('recipients_step.phone_label')}</label>
          <input className="input" {...register('phone')} placeholder={t('recipients_step.phone_placeholder')} />
        </div>
      )}
      {authMethod === 'access_code' && (
        <div className="mb-3">
          <label className="label">{t('recipients_step.access_code_label')}</label>
          <input className="input" {...register('accessCode')} placeholder={t('recipients_step.access_code_placeholder')} />
          {errors.accessCode && <p className="text-xs text-danger mt-1">{errors.accessCode.message}</p>}
          <p className="help">{t('recipients_step.access_code_help')}</p>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="text-[12px] text-muted hover:text-gray-900 mb-2"
      >
        {showAdvanced ? '▼' : '▶'} {t('recipients_step.custom_message')}
      </button>
      {showAdvanced && (
        <div className="mb-3">
          <textarea
            className="textarea"
            rows={3}
            {...register('customMessage')}
            placeholder={t('recipients_step.custom_message_placeholder')}
          />
        </div>
      )}

      <div className="flex justify-end pt-3 border-t border-border">
        <button type="submit" className="btn btn-primary" disabled={mutation.isPending}>
          {mutation.isPending ? t('recipients_step.adding') : t('recipients_step.add_recipient_btn')}
        </button>
      </div>
    </form>
  );
}

function TeamPicker({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (u: { firstName: string; lastName: string; email: string }) => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => usersApi.list(),
  });
  const filtered = users.filter((u) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      u.email.toLowerCase().includes(needle) ||
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(needle)
    );
  });
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="text-base font-semibold">{t('recipients_step.team_picker_title')}</h3>
          <button onClick={onClose} className="text-muted hover:text-gray-900 text-xl leading-none">
            ×
          </button>
        </div>
        <div className="p-4 border-b border-border">
          <input
            autoFocus
            className="input"
            placeholder={t('recipients_step.team_picker_search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {isLoading ? (
            <div className="text-center py-8 text-muted text-[13px]">{t('recipients_step.team_picker_loading')}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-muted text-[13px]">
              {q ? t('recipients_step.team_picker_no_match') : t('recipients_step.team_picker_empty')}
            </div>
          ) : (
            <ul className="space-y-1">
              {filtered.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => onPick(u)}
                    className="w-full text-left flex items-center gap-3 p-2 rounded-md hover:bg-gray-100"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-[11px] font-semibold shrink-0">
                      {(u.firstName[0] ?? '').toUpperCase()}{(u.lastName[0] ?? '').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-gray-900 truncate">
                        {u.firstName} {u.lastName}
                      </div>
                      <div className="text-[11px] text-muted truncate">{u.email}</div>
                    </div>
                    <span className="badge badge-muted text-[10px]">{u.role}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
